import { BuildEnvironment } from "@base/BuildEnvironment";
import { IVec3 } from "@base/math/Vec3";
import { IdGen } from "@base/util/IdGen";
import { IArticle } from "@biz/definitions/ArticleDefinitions";
import {
    ILoungeData, ILoungeItem, ILoungeItemTypeInfo, ILoungeReloadData, ILoungeReloadItem, ILoungeUnpluggedData, ILoungeUnpluggedItem, IReloadChairSide, LoungeItemType, LoungeItemTypeInfos, LoungeType, ReloadArmRestType, ReloadMaterial, ReloadType,
    sojoInfos, SojoTemplateId, sojoTemplateIds, UnpluggedType
} from "@biz/definitions/LoungeDefinitions";
import { ModelData, ModelTemplateId } from "@biz/definitions/ModelDefinitions";
import { IModelProductData } from "@biz/definitions/ModelProductDefinitions";
import { FeatureLevel } from "@biz/definitions/ProductDefinitions";
import { IClothData } from "@biz/logic/ClothData";
import { ClothDataLogic } from "@biz/logic/ClothDataLogic";
import { ShoppingList } from "@biz/models/ShoppingList";
import { IColorItem } from "@biz/SunSailColors";

export class ReloadData {
  static createItem(options?: { itemType?: ReloadType }): ILoungeReloadItem {
    const itemType = options?.itemType || "75";
    const reloadChair: ILoungeReloadItem = {
      itemType,
    }
    if (ReloadData.isPillowPossibleForType(itemType))
      reloadChair.pillow = true
    if (ReloadData.isArmRestPossibleForType(itemType))
      reloadChair.armRest = { /*left: "bar", right: "bar"*/ }; // auto
    return reloadChair;
  }

  static create(pos: IVec3, featureLevel: FeatureLevel, articleMap: Record<string, IArticle>, colorMap: Record<string, IColorItem>, options?: { itemType?: ReloadType }): ILoungeReloadData {
    const loungeType: LoungeType = "Reload";

    const chair = ReloadData.createItem(options);

    const defaultClothData = ClothDataLogic.getDefaultClothData({ pdt: "lounge", loungeType, version: "2022" }, featureLevel);

    return {
      id: IdGen.unsecure.get(),
      version: "2022",
      buildTime: BuildEnvironment.BuildTimeAsNumber,
      featureLevel,
      itemType: "lounge",
      loungeType,
      position: pos,
      rotationY: 0,
      count: 1,
      shoppingList: ShoppingList.createEmpty(),
      orderType: "H",
      global: {
        cloth: defaultClothData,
        colorId: defaultClothData ? ClothDataLogic.getInitialColorId(defaultClothData, articleMap, colorMap, featureLevel) : "",
        material: "zinc"
      },
      chains: [{ items: [chair] }],
      // armRest: { /*left: "bar", right: "bar"*/ },
      armRest: { left: "bar", right: "bar" },
      sidePillow: {},
      sourceConfigurator: "3d",
    }
  }

  static getReloadMaterial(data: ILoungeReloadData, itemIndex: number): ReloadMaterial {
    return LoungeData.getItem(data, itemIndex)?.material || data.global.material || "zinc";
  }

  static isArmRestPossibleForType(itemType: LoungeItemType): boolean {
    return !!LoungeData.getLoungeItemTypeInfo(itemType)?.armRestPossible;
  }

  static #getArmRest(data: ILoungeReloadData, itemIndex: number, side: IReloadChairSide): ReloadArmRestType | undefined {
    const possible = ReloadData.isArmRestPossible(data, itemIndex, side);
    if (!possible)
      return undefined;
    return data.armRest?.[side];
    /*const chair = LoungeData.getItem(data, itemIndex);
    const armRest = chair.armRest?.[side]
    return armRest;*/
  }

  static #mergeArmRest(a: ReloadArmRestType | undefined, b: ReloadArmRestType | undefined): ReloadArmRestType | undefined {
    if (a === b)
      return a;
    if (a)
      return a;
    if (b)
      return b;
    return undefined;
  }

  static getArmRest(data: ILoungeReloadData, itemIndex: number, side: IReloadChairSide): ReloadArmRestType | undefined {
    if (side === "left")
      return ReloadData.#mergeArmRest(ReloadData.#getArmRest(data, itemIndex, "left"), data.chains[0].items.length > itemIndex + 1 ? ReloadData.#getArmRest(data, itemIndex + 1, "right") : undefined)
    return ReloadData.#mergeArmRest(ReloadData.#getArmRest(data, itemIndex, "right"), itemIndex > 0 ? ReloadData.#getArmRest(data, itemIndex - 1, "left") : undefined)
  }

  static isArmRestPossible(data: ILoungeReloadData, itemIndex: number, side: IReloadChairSide): boolean {
    const chair = LoungeData.getItem(data, itemIndex);
    const chairCount = LoungeData.getItems(data).length;
    if (side === "left") {
      const next = itemIndex < chairCount - 1 ? LoungeData.getItem(data, itemIndex + 1) : undefined
      // return ReloadData.isArmRestPossibleForType(chair.itemType) && (!next || ReloadData.isArmRestPossibleForType(next?.itemType))
      return ReloadData.isArmRestPossibleForType(chair.itemType) && !next;
    } else {
      const last = itemIndex > 0 ? LoungeData.getItem(data, itemIndex - 1) : undefined
      // return ReloadData.isArmRestPossibleForType(chair.itemType) && (!last || ReloadData.isArmRestPossibleForType(last?.itemType))
      return ReloadData.isArmRestPossibleForType(chair.itemType) && !last;
    }
  }

  static isSidePillowPossible(data: ILoungeReloadData, itemIndex: number, side: IReloadChairSide): boolean {
    if (itemIndex !== 0 && side === "right")
      return false;
    if (itemIndex !== data.chains[0].items.length - 1 && side === "left")
      return false;
    if (ReloadData.getArmRest(data, itemIndex, side) !== "bar")
      return false;
    return true;
  }

  static getSidePillow(data: ILoungeReloadData, itemIndex: number, side: IReloadChairSide): boolean {
    if (!ReloadData.isSidePillowPossible(data, itemIndex, side))
      return false;
    // const chair = LoungeData.getItem(data, itemIndex);
    // return !!chair.sidePillow?.[side];
    return !!data.sidePillow?.[side];
  }

  static getPillow(data: ILoungeReloadData, itemIndex: number): boolean {
    const chair = LoungeData.getItem(data, itemIndex);
    return ReloadData.isPillowPossibleForType(chair.itemType) && !!chair.pillow;
  }

  static isPillowPossibleForType(itemType: LoungeItemType): boolean {
    return !!LoungeData.getLoungeItemTypeInfo(itemType)?.pillowPossible;
  }

  static isTable(reloadType: ReloadType): boolean {
    return reloadType.startsWith("Table");
  }

  static hasColor(reloadType: ReloadType): boolean {
    return !reloadType.startsWith("Table");
  }

  static calcReloadItemArticleNumber(reloadType: ReloadType, clothGroup: string, material: ReloadMaterial, armRest?: { left: boolean, right: boolean }): string {
    const isTable = ReloadData.isTable(reloadType);
    const reloadTypeLetter = LoungeItemTypeInfos[reloadType].articlePriceListId;
    const armRestLetter = ReloadData.isArmRestPossibleForType(reloadType) ? ["M", "L", "R", "S"][(armRest?.left ? 1 : 0) + (armRest?.right ? 2 : 0)] : "";
    const materialLetter = material === "steel" ? "ED" : (isTable ? "PB" : "VZ");
    return `RL${materialLetter}${reloadTypeLetter}${armRestLetter}${isTable ? "" : clothGroup}`;
  }

  static calcReloadSidePillowArticleNumber(clothGroup: string): string {
    return `RL${clothGroup}PIL04`;
  }

  static getArticleNumbers(data: ILoungeReloadData, itemIndex: number): { articleNumber: string, colorId?: string }[] {
    const articleNumbers: { articleNumber: string, colorId?: string }[] = [];

    const chair = LoungeData.getItem(data, itemIndex);
    const clothGroup = LoungeData.getItemClothGroup(data, itemIndex);
    const material = ReloadData.getReloadMaterial(data, itemIndex)
    const armRestLeft = ReloadData.getArmRest(data, itemIndex, "left")
    const armRestRight = ReloadData.getArmRest(data, itemIndex, "right")
    const itemType = chair.itemType;
    const colorId = LoungeData.getItemColorId(data, itemIndex);

    {
      articleNumbers.push({ articleNumber: this.calcReloadItemArticleNumber(itemType, clothGroup, material, { left: !!armRestLeft, right: !!armRestRight }), colorId: this.hasColor(itemType) ? colorId : "" });
    }
    // if (ReloadData.getPillow(data, itemIndex)) {
    //   const itemType = chair.itemType;
    //   const reloadTypeLetter: Record<ReloadType, string> = {
    //     "75": "01",
    //     "100": "02",
    //     "75_Eck": "03",
    //     "Table_300": "",
    //     "Table_600": "",
    //     "Table_Lounge": "",
    //   }
    //   articleNumbers.push({ articleNumber: "RL936PIL" + reloadTypeLetter[itemType], colorId: ReloadData.getItemColorId(data, itemIndex) });
    // }
    const pillowLeft = ReloadData.getSidePillow(data, itemIndex, "left")
    const pillowRight = ReloadData.getSidePillow(data, itemIndex, "right")
    if (pillowLeft) {
      articleNumbers.push({ articleNumber: ReloadData.calcReloadSidePillowArticleNumber(clothGroup), colorId })
    }
    if (pillowRight) {
      articleNumbers.push({ articleNumber: ReloadData.calcReloadSidePillowArticleNumber(clothGroup), colorId })
    }
    return articleNumbers;
  }

  static hasCloth(ld: ILoungeReloadData, index: number): boolean {
    const item = LoungeData.getItem(ld, index);
    const pillowPossible = ReloadData.isPillowPossibleForType(item.itemType);
    const armRestLeft = ReloadData.isArmRestPossible(ld, index, "left");
    const armRestRight = ReloadData.isArmRestPossible(ld, index, "right");
    const pillowLeft = ReloadData.isSidePillowPossible(ld, index, "left");
    const pillowRight = ReloadData.isSidePillowPossible(ld, index, "right");
    const pillow = ReloadData.getPillow(ld, index);

    return (pillowPossible && ((armRestRight && pillowRight) || (armRestLeft && pillowLeft) || pillow))
  }
}

export class LoungeData {
  static clothArticleId = "RL936BEZ01";

  static getItems<T extends ILoungeData>(data: T): T["chains"][0]["items"] {
    return data.chains?.[0].items || [];
  }

  static getItem<T extends ILoungeData>(data: T, itemIndex: number): T["chains"][0]["items"][0] {
    return LoungeData.getItems(data)[itemIndex];
  }

  static getItemCloth(data: ILoungeData, itemIndex: number): IClothData | undefined {
    return LoungeData.getItem(data, itemIndex)?.cloth || data.global.cloth;
  }

  static getItemColorId(data: ILoungeData, itemIndex: number): string {
    return LoungeData.getItem(data, itemIndex)?.colorId || data.global.colorId;
  }

  static getItemClothGroup(data: ILoungeData, itemIndex: number): string {
    const cloth = LoungeData.getItemCloth(data, itemIndex);
    return cloth?.articleId?.group ?? "936";
  }

  static createItem(loungeType: LoungeType): ILoungeItem {
    switch (loungeType) {
      case "Reload":
        return ReloadData.createItem();
      case "Unplugged":
        return UnpluggedData.createItem();
    }
    throw new Error("Invalid loungeType!");
  }

  static isStandalone(itemType: LoungeItemType): boolean {
    return !!LoungeData.getLoungeItemTypeInfo(itemType)?.standalone;
  }

  static getLoungeItemTypeInfo(itemType: LoungeItemType): ILoungeItemTypeInfo | undefined {
    return LoungeItemTypeInfos[itemType];
  }

  static getLoungeItemTypeInfos(): Record<LoungeItemType, ILoungeItemTypeInfo> {
    return LoungeItemTypeInfos;
  }

  static calcItemWidth(itemtype: LoungeItemType): number {
    return LoungeItemTypeInfos[itemtype]?.width || 2;
  }

  static calcPillowIndex(templateId: ModelTemplateId): string {
    switch (templateId) {
      case "pillow_40x40": return "01";
      case "pillow_40x80": return "02";
      case "pillow_75x75": return "03";
    }
    throw new Error("Not supported so far!");
  }

  static calcLoungeClothGroup(cloth: IClothData | undefined): string {
    return cloth?.articleId?.group ?? "936";

  }

  static calcPillowArticleNumber(product: IModelProductData): string {
    const pillowIndex = LoungeData.calcPillowIndex(product.templateId);
    return `PIL${LoungeData.calcLoungeClothGroup(product.configuration?.cloth)}G${pillowIndex}`;
  }

  static calcChilloutArticleNumber(product: IModelProductData): string {
    const logo = ModelData.getLogo(product);
    return `CO${LoungeData.calcLoungeClothGroup(product.configuration?.cloth)}${logo ? "A02" : "A01"}`;
  }

  static calcStoolArticleNumber(product: IModelProductData): string {
    const logo = ModelData.getLogo(product);
    return `STO${LoungeData.calcLoungeClothGroup(product.configuration?.cloth)}${logo ? "G01P" : "G01"}`;
  }

  static calcSojoHardwareArticleNumber(product: IModelProductData): string {
    return `${sojoInfos[product.templateId as SojoTemplateId]?.hw}${LoungeData.calcLoungeClothGroup(product.configuration?.cloth)}`;
  }

  static calcSojoClothArticleNumber(product: IModelProductData): string {
    return `${sojoInfos[product.templateId as SojoTemplateId]?.cloth}${LoungeData.calcLoungeClothGroup(product.configuration?.cloth)}`;
  }

  static calcBeanbagArticleNumber(product: IModelProductData): string {
    const logo = ModelData.getLogo(product);
    return logo ? "BEAN93602" : "BEAN93601"
  }

  static calcDropArticleNumber(product: IModelProductData): string {
    return `PEAR${LoungeData.calcLoungeClothGroup(product.configuration?.cloth)}`;
  }
  static calcCatArticleNumber(product: IModelProductData): string {
    return `CAT${LoungeData.calcLoungeClothGroup(product.configuration?.cloth)}`;
  }

  static isSojo(templateId: ModelTemplateId): boolean {
    return sojoTemplateIds.includes(templateId as SojoTemplateId);
  }
}

export class UnpluggedData {
  static createItem(options?: { itemType?: UnpluggedType }): ILoungeUnpluggedItem {
    const itemType = options?.itemType || "SS";
    const item: ILoungeUnpluggedItem = {
      itemType,
    }
    return item;
  }

  static create(pos: IVec3, featureLevel: FeatureLevel, articleMap: Record<string, IArticle>, colorMap: Record<string, IColorItem>, options?: { itemType?: UnpluggedType }): ILoungeUnpluggedData {
    const loungeType: LoungeType = "Unplugged";

    const chair = UnpluggedData.createItem(options);

    const defaultClothData = ClothDataLogic.getDefaultClothData({ pdt: "lounge", loungeType, version: "2022" }, featureLevel);

    return {
      id: IdGen.unsecure.get(),
      version: "2022",
      buildTime: BuildEnvironment.BuildTimeAsNumber,
      featureLevel,
      itemType: "lounge",
      loungeType,
      position: pos,
      rotationY: 0,
      count: 1,
      shoppingList: ShoppingList.createEmpty(),
      orderType: "H",
      sourceConfigurator: "3d",
      global: {
        cloth: defaultClothData,
        colorId: defaultClothData ? ClothDataLogic.getInitialColorId(defaultClothData, articleMap, colorMap, featureLevel) : "",
      },
      chains: [{ items: [chair] }]
    }
  }

  static getItemColorId(data: ILoungeUnpluggedData, itemIndex: number): string {
    return LoungeData.getItem(data, itemIndex)?.colorId || data.global.colorId;
  }

  static getArticleNumbers(data: ILoungeUnpluggedData, itemIndex: number): { articleNumber: string, colorId?: string }[] {
    const articleNumbers: { articleNumber: string, colorId?: string }[] = [];

    const chair = LoungeData.getItem(data, itemIndex);
    const clothGroup = LoungeData.getItemClothGroup(data, itemIndex);
    const itemType = chair.itemType;
    const articleNumber = `UP${clothGroup}${itemType}`;
    if (articleNumber) {
      articleNumbers.push({ articleNumber, colorId: LoungeData.getItemColorId(data, itemIndex) });
    }
    return articleNumbers;
  }
}